import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MDBCol, MDBCard, MDBCardBody, MDBCardHeader, MDBCardTitle } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { Button, Row } from "../App.styles";
import { FormGroup, SigninForm } from "../sign-in/SignIn.styles";
import AlertDisplay from "../common/AlertDisplay";
import { Input } from "../common/Input";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { unlockCodeState } from "../monitor/Monitor.state";
import { useRecoilState } from "recoil";
import { useAuthUser } from "react-auth-kit";

const GameCodeValidationForm = ({ validationState }) => {
  const { t } = useTranslation();
  const [code, setCode] = useRecoilState(unlockCodeState);
  const location = useLocation();
  const [, setError] = useRecoilState(alertState);
  const [, setIsValidated] = validationState;

  useEffect(() => {
    // Prepopulate code from query param if present
    try {
      const params = new URLSearchParams(location.search);
      const prefill = params.get('code');
      if (prefill && prefill !== code) {
        setCode(prefill);
        setError({ type: "none", message: "" });
      }
    } catch {}
  }, [location.search, code, setCode, setError]);

  return (
    <>
      <Row>
        <MDBCol size="12">
          <AlertDisplay />
        </MDBCol>
      </Row>
      <Row>
        <MDBCol size="12">
          <MDBCard>
            <MDBCardHeader>
              <MDBCardTitle>
                <span dangerouslySetInnerHTML={{ __html: t('Enter Code') }} />
              </MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
              <SigninForm
                data-testid="form-signincode"
                onSubmit={(e) => {
                  e.preventDefault();
                  setIsValidated(true);
                  setError({ type: "none", message: "" });
                }}
              >
                <FormGroup>
                  <Input
                    data-testid="form-signincode__field-code"
                    className="form-control active"
                    label={t('Code')}
                    value={code}
                    onChange={(value) => setCode(value)}
                  />
                </FormGroup>
                <Button data-testid="form-signincode__btn-continue" color="primary" type="submit">
                  <span dangerouslySetInnerHTML={{ __html: t('Continue') }} />
                </Button>
              </SigninForm>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </Row>
    </>
  );
};

/**
 * A Higher-Order Component (HOC) that protects a route by requiring a valid game code.
 * If the user is authenticated, they bypass this check. If they are unauthenticated,
 * they are presented with a form to enter the game code before the wrapped component is rendered.
 * 
 * @param {React.ComponentType} Component - The component to protect.
 * @returns {React.FC} A new component that handles the game code validation logic.
 */
export const withValidateGameCode = (Component) => {
  return function WrappedComponent(props) {
    const auth = useAuthUser()();
    const [isValidated, setIsValidated] = useState(false);

    if (!isValidated && !auth) {
      return <GameCodeValidationForm validationState={[isValidated, setIsValidated]} />;
    }

    return <Component {...props} validationState={[isValidated, setIsValidated]} />;
  };
};

export default withValidateGameCode;


